import styleVariables from '@/styles/style.module.scss';
import { Box, Typography, Button } from '@mui/material';
import CaptureManager from './CaptureManager';
import ScannerInputField, { ScannerInputFieldRef } from './ScannerInputField';
import { useRef, useLayoutEffect, useMemo, useCallback, useEffect, KeyboardEvent, FC } from 'react';
import { ButtonInfo } from '../provider/state';
import { MobileFeatureHandle, BUTTON_STATUS_VIEW_MAP, MobileFeatureComponent } from '../types';
import { DUPLICATE_TTL_MS, COOL_DOWN_MS } from '../utils/mobileUtils';
import History, { HistoryItemProps } from './History';
import { useMobileState } from '../provider/context';
import { useTranslation } from 'react-i18next';
import useSoundPlayer from '../hooks/useSoundPlayer';

const AUTO_RESET_STATUS: ButtonInfo['type'][] = ['success', 'error'];
type EslPairingFlowProps = {
    FeatureView: MobileFeatureComponent;
};
const EslPairingFlow: FC<EslPairingFlowProps> = ({ FeatureView }) => {
    const { t } = useTranslation('common');
    const { playError, playScan, playSuccess } = useSoundPlayer();

    const { state, dispatch } = useMobileState();
    const { settings } = state.storeState;
    const isSendingRef = useRef(false);
    const scanGateRef = useRef({
        locked: false,
        lastValue: '',
        lastAt: 0,
    });
    const activeFeatureRef = useRef<MobileFeatureHandle | null>(null);

    const eslMacInputRef = useRef<ScannerInputFieldRef | null>(null);
    const lastManualEnterRef = useRef<{ value: string; at: number }>({ value: '', at: 0 });
    const buttonInfoTimeoutRef = useRef<number | null>(null);

    const focusCommonEslInput = useCallback(() => {
        eslMacInputRef.current?.focus();
    }, []);

    useLayoutEffect(() => {
        focusCommonEslInput();
    }, [focusCommonEslInput]);

    useEffect(() => {
        return () => clearButtonInfoTimeout();
    }, []);

    useEffect(() => {
        dispatch({ type: 'SET_ESL', payload: '' });
        dispatch({ type: 'SET_BUTTON_INFO', payload: { type: 'idle' } });
        focusCommonEslInput();
    }, [dispatch, focusCommonEslInput, state.activeTab]);

    const buttonStatus = useMemo(() => {
        const featureStatus = state.buttonInfo;
        if (!featureStatus) {
            return BUTTON_STATUS_VIEW_MAP.idle;
        }
        const baseView = BUTTON_STATUS_VIEW_MAP[featureStatus.type];
        if (featureStatus.type === 'error') {
            return {
                ...baseView,
                msg: featureStatus.msg || baseView.msg,
            };
        }

        return baseView;
    }, [state.buttonInfo]);

    const isEnterLikeEvent = (event: KeyboardEvent<HTMLInputElement>) => {
        const nativeEvent = event.nativeEvent as unknown as {
            key?: string;
            code?: string;
            keyCode?: number;
            which?: number;
        };

        return (
            event.key === 'Enter' ||
            event.key === 'NumpadEnter' ||
            event.key === 'LineFeed' ||
            nativeEvent.key === 'Enter' ||
            nativeEvent.code === 'Enter' ||
            nativeEvent.code === 'NumpadEnter' ||
            nativeEvent.keyCode === 13 ||
            nativeEvent.which === 13
        );
    };

    const triggerEslEnter = async (event: KeyboardEvent<HTMLInputElement>) => {
        if (isSendingRef.current) return;
        if (!isEnterLikeEvent(event)) return;

        const latestValue = (event.currentTarget as HTMLInputElement).value;
        const now = Date.now();
        const last = lastManualEnterRef.current;
        if (latestValue === last.value && now - last.at < 180) {
            event.preventDefault();
            return;
        }

        lastManualEnterRef.current = { value: latestValue, at: now };
        event.preventDefault();

        await activeFeatureRef.current?.onEslEnter(latestValue);
    };

    const onEslKeyDown = useCallback(async (event: KeyboardEvent<HTMLInputElement>) => {
        await triggerEslEnter(event);
    }, []);

    const handleResetAll = () => {
        isSendingRef.current = false;
        dispatch({ type: 'SET_BUTTON_INFO', payload: { type: 'idle' } });
        dispatch({ type: 'SET_ESL', payload: '' });

        if (activeFeatureRef.current?.onClear) {
            activeFeatureRef.current.onClear();
        }
    };

    const handleBarcodeScanned = useCallback(async (barcodeWithNewline: string) => {
        await activeFeatureRef.current?.onScan(barcodeWithNewline);
    }, []);

    const runScanThroughGate = useCallback(
        async (barcodeWithNewline: string) => {
            const now = performance.now();
            if (!barcodeWithNewline) return;

            const gate = scanGateRef.current;
            if (gate.locked) return;

            // Ignore duplicated scanner output that arrives within the TTL window.
            if (barcodeWithNewline === gate.lastValue && now - gate.lastAt < DUPLICATE_TTL_MS) return;

            gate.locked = true;
            try {
                await handleBarcodeScanned(barcodeWithNewline);
            } finally {
                gate.lastValue = barcodeWithNewline;
                gate.lastAt = performance.now();
                window.setTimeout(() => {
                    gate.locked = false;
                }, COOL_DOWN_MS);
            }
        },
        [handleBarcodeScanned],
    );

    const handleOnBarcodeDetected = useCallback(
        async (barcode: string) => {
            const trimmedBarcode = barcode.replace(/(\r\n|\r|\n)+$/g, '');
            const barcodeWithNewline = `${trimmedBarcode}\n`;
            await runScanThroughGate(barcodeWithNewline);
        },
        [runScanThroughGate],
    );

    const handleModeChanged = useCallback(() => {
        activeFeatureRef.current?.onModeChange();
        focusCommonEslInput();
    }, [focusCommonEslInput]);

    const setESL = useCallback((val: string) => dispatch({ type: 'SET_ESL', payload: val }), []);

    const clearButtonInfoTimeout = useCallback(() => {
        if (buttonInfoTimeoutRef.current !== null) {
            window.clearTimeout(buttonInfoTimeoutRef.current);
            buttonInfoTimeoutRef.current = null;
        }
    }, []);

    const setButtonInfo = useCallback(({ type, msg }: ButtonInfo) => {
        clearButtonInfoTimeout();

        const resolvedMessage = type === 'error' ? (msg ?? 'Unknown error occurred') : msg;

        dispatch({
            type: 'SET_BUTTON_INFO',
            payload: {
                type,
                msg: resolvedMessage,
            },
        });

        switch (type) {
            case 'loading':
                playScan();
                return;

            case 'error':
                playError();
                dispatch({ type: 'SET_ESL', payload: '' });
                break;

            case 'success':
                playSuccess();
                break;

            case 'sending':
            case 'idle':
            default:
                break;
        }

        if (AUTO_RESET_STATUS.includes(type)) {
            buttonInfoTimeoutRef.current = window.setTimeout(
                () => {
                    dispatch({
                        type: 'SET_BUTTON_INFO',
                        payload: { type: 'idle' },
                    });
                    buttonInfoTimeoutRef.current = null;
                },
                type === 'error' ? 2500 : 1000,
            );
        }
    }, []);

    const addHistoryItem = useCallback((item: HistoryItemProps) => {
        dispatch({ type: 'ADD_HISTORY_ITEM', payload: item });
    }, []);

    const dependency = useMemo(
        () => ({
            settings,
            refs: {
                isSendingRef,
                eslMacInputRef,
            },
            method: {
                playError,
                playScan,
                playSuccess,
                setESL,
                setButtonInfo,
                addHistoryItem,
                focusCommonEslInput,
            },
        }),
        [settings, addHistoryItem, playError, playScan, playSuccess, setButtonInfo, setESL, focusCommonEslInput],
    );
    return (
        <>
            <CaptureManager
                isSendingRef={isSendingRef}
                eslMacInputRef={eslMacInputRef}
                onBarcodeDetected={handleOnBarcodeDetected}
                onModeChanged={handleModeChanged}
            />

            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    gap: 1,
                    px: 2,
                    pb: 2,
                }}
            >
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {t(state.activeTab === 'out-of-stock' ? 'Barcode' : 'ESL MAC')}
                    </Typography>
                    <ScannerInputField
                        ref={eslMacInputRef}
                        value={state.esl}
                        setValue={setESL}
                        onClear={handleResetAll}
                        onKeyDown={onEslKeyDown}
                        onScannerScan={handleBarcodeScanned}
                        inputTestId="mobile-esl-input"
                    />
                </Box>
                <FeatureView ref={activeFeatureRef} dependency={dependency} />
                <Button
                    data-testid="mobile-status-banner"
                    data-status={state.buttonInfo.type}
                    sx={{
                        width: '100%',
                        p: '8px 16px',
                        borderRadius: 1,
                        backgroundColor: buttonStatus.bgColor,
                        color: styleVariables.textColor,
                        cursor: 'not-allowed',
                    }}
                    disabled={true}
                >
                    <Typography variant="body2" fontWeight="bold">
                        {t(buttonStatus.msg)}
                    </Typography>
                </Button>
                <History historyItems={state.historyItems} addLeadingZero={settings.addLeadingZero === '1'} />
            </Box>
        </>
    );
};

export default EslPairingFlow;
